import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SG_GET_STAFF_REPORTS,
  UPDATE_STAFF_REPORT_PAGE_NUMBER,
  UPDATE_STAFF_REPORT_QUERY_PARAMS,
  SG_INVALIDATE_STAFF_CACHE,
} from 'constants/actions';
import PageIntro from 'action/components/PageIntro';
import PreGenerateModal from './PreGenerateModal';
import CheckCacheStatusModal from './CheckCacheStatusModal';
import { Link } from 'react-router-dom';

export default function ReportsList() {
  const dispatch = useDispatch();
  const data = useSelector((s) => s.staff_reports || {});
  const page = data.pageNumber || 1;
  const reports = Array.isArray(data.reports?.reports) ? data.reports.reports : (Array.isArray(data.reports) ? data.reports : []);
  const total = data.reports?.total ?? (reports.length || 0);

  const [loading, setLoading] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [auditTypeId, setAuditTypeId] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [preGenOpen, setPreGenOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusOrg, setStatusOrg] = useState(null);
  const [statusAudit, setStatusAudit] = useState(null);

  useEffect(() => {
    // combine current page + saved query params
    setLoading(true);
    const base = data.queryParams || '';
    const pagePart = `page=${page}`;
    const sizePart = `page_size=${pageSize}`;
    const q = [pagePart, sizePart, base].filter(Boolean).join('&');
    dispatch({ type: SG_GET_STAFF_REPORTS, payload: q });
  }, [dispatch, page, data.queryParams, pageSize]);

  console.log('preGenOpen', preGenOpen);
  // when stored query params change, reflect them in the local form
  useEffect(() => {
    try {
      const params = new URLSearchParams(data.queryParams || '');
      setOrganizationId(params.get('organization_id') || '');
      setEmployeeId(params.get('employee_id') || '');
      setEmployeeEmail(params.get('employee_email') || '');
      setAuditTypeId(params.get('audit_type_id') || '');
      setSearch(params.get('search') || '');
      const s = params.get('page_size');
      if (s) setPageSize(Number(s));
    } catch (e) {
      // ignore parse errors
    }
  }, [data.queryParams]);

  useEffect(() => setLoading(false), [data.reports]);

  function prev() {
    if (page > 1) {
      dispatch({ type: UPDATE_STAFF_REPORT_PAGE_NUMBER, payload: page - 1 });
    }
  }

  function next() {
    dispatch({ type: UPDATE_STAFF_REPORT_PAGE_NUMBER, payload: page + 1 });
  }

  // Pre-generate action is handled by PreGenerateModal now

  function invalidate(orgId, auditType) {
    const q = `organization_id=${orgId}&audit_type_id=${auditType}`;
    dispatch({ type: SG_INVALIDATE_STAFF_CACHE, payload: q });
  }

  // checkStatus will be handled by the status modal component when opened

  return (
    <>
    <div>
      <PageIntro title="Staff reports" description="Manage cached reports (pre-generate, inspect, invalidate)." bullets={[ 'Use pre-generate to warm cache.', 'Monitor status and invalidate when necessary.' ]} />

      {loading ? <div style={{ color: '#666' }}>Loading...</div> : null}

      <div style={{ marginTop: 12, padding: 12, border: '1px solid #efefef', borderRadius: 6 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#333' }}>Org</span>
            <input aria-label="Organization id" placeholder="Organization id" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)} />
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#333' }}>Emp</span>
            <input aria-label="Employee id" placeholder="Employee id" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#333' }}>Email</span>
            <input aria-label="Employee email" placeholder="Employee email" value={employeeEmail} onChange={(e) => setEmployeeEmail(e.target.value)} />
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#333' }}>Audit</span>
            <input aria-label="Audit type id" placeholder="Audit type id" value={auditTypeId} onChange={(e) => setAuditTypeId(e.target.value)} />
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#333' }}>Search</span>
            <input aria-label="Search cache key" placeholder="Search cache_key" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <button aria-label="Apply filters" onClick={() => {
              const parts = [];
              if (organizationId) parts.push(`organization_id=${organizationId}`);
              if (employeeId) parts.push(`employee_id=${employeeId}`);
              if (employeeEmail) parts.push(`employee_email=${encodeURIComponent(employeeEmail)}`);
              if (auditTypeId) parts.push(`audit_type_id=${auditTypeId}`);
              if (search) parts.push(`search=${encodeURIComponent(search)}`);
              const combined = parts.join('&');
              dispatch({ type: UPDATE_STAFF_REPORT_QUERY_PARAMS, payload: combined });
              // reset to first page
              dispatch({ type: UPDATE_STAFF_REPORT_PAGE_NUMBER, payload: 1 });
            }}>Apply</button>
            <button aria-label="Reset filters" onClick={() => {
              setOrganizationId(''); 
              setEmployeeId(''); 
              setEmployeeEmail(''); 
              setAuditTypeId(''); 
              setSearch(''); 
              setPageSize(20);
              dispatch({ type: UPDATE_STAFF_REPORT_QUERY_PARAMS, payload: '' });
              dispatch({ type: UPDATE_STAFF_REPORT_PAGE_NUMBER, payload: 1 });
            }}>Reset</button>
          </div>
        </div>
      </div>

      {/* Action Blocks */}
      <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* Pre-generate Block */}
        <div style={{ flex: '1 1 300px', padding: 12, border: '1px solid #e0e0e0', borderRadius: 6, background: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>Pre-generate Cache</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#666' }}>Warm cache for specific organization/employee combinations</p>
          <button
            aria-label="Pre-generate cache"
            onClick={() => setPreGenOpen(true)}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#0946d6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#0b5fff'; }}
            style={{ background: '#0b5fff', color: 'white', padding: '8px 16px', borderRadius: 4, border: 'none', cursor: 'pointer', fontWeight: 500 }}
          >
            Open Pre-generate
          </button>
        </div>

        {/* Other Actions Block */}
        <div style={{ flex: '1 1 300px', padding: 12, border: '1px solid #e0e0e0', borderRadius: 6, background: '#f9f9f9' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600 }}>Cache Management</h4>
          <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#666' }}>Check status or invalidate cache for specific reports</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { 
                const org = organizationId || null;
                const audit = auditTypeId || '3';
                if (org) {
                  setStatusOrg(org); 
                  setStatusAudit(audit); 
                  setStatusOpen(true); 
                }
              }}
              style={{ background: '#6c757d', color: '#fff', padding: '8px 16px', borderRadius: 4, border: 'none', cursor: organizationId ? 'pointer' : 'not-allowed', opacity: organizationId ? 1 : 0.5, fontWeight: 500 }}
              aria-label="Check cache status"
              disabled={!organizationId}
              title={!organizationId ? "Set Organization ID in filters above" : "Check cache status"}
            >
              Check Status
            </button>
          </div>
        </div>
      </div>

      <div role="list" aria-label="Reports list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {reports.length ? (
          reports.map((r) => (
            <div key={r.id} role="listitem" style={{ padding: 12, border: '1px solid #eee', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{r.cache_key}</div>
                  <div style={{ color: '#666', fontSize: 13 }}>{r.organization_id} • emp {r.employee_id} • audit {r.audit_type_id || 'n/a'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Link to={`${r.id}`} style={{ color: '#0b5fff', fontWeight: 700 }}>View</Link>
                
                  <button aria-label={`Invalidate cache for ${r.id}`} onClick={() => invalidate(r.organization_id, r.audit_type_id)} style={{ fontSize: 12 }}>Invalidate</button>
                  <button aria-label={`Check cache status for ${r.id}`} onClick={() => { setStatusOrg(r.organization_id); setStatusAudit(r.audit_type_id || 3); setStatusOpen(true); }} style={{ fontSize: 12 }}>Status</button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: 18, border: '1px dashed #eee', borderRadius: 8, color: '#666' }}>No cached reports</div>
        )}
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={prev} disabled={page <= 1}>Prev</button>
        <span>Page {page} — Total {total}</span>
        <button onClick={next} disabled={total && page >= Math.max(1, Math.ceil(total / pageSize))}>Next</button>
      </div>

      <PreGenerateModal open={preGenOpen} onClose={() => setPreGenOpen(false)} defaultOrg={organizationId || null} defaultEmp={employeeId || null} />
      <CheckCacheStatusModal open={statusOpen} onClose={() => setStatusOpen(false)} organizationId={statusOrg} auditTypeId={statusAudit} />
    </div>
    </>
  );
}
